import React, { useEffect, useState } from 'react';
import './ProgressSidebar.css';

function ProgressSidebar({ activeStepId, steps }) {
  const [remainingTime, setRemainingTime] = useState(0);
  const [completionPercent, setCompletionPercent] = useState(0);

  // Calculate total recipe time (in seconds)
  const totalRecipeTime = steps.reduce((total, step) => total + step.duration, 0);

  useEffect(() => {
    if (!activeStepId) {
      setCompletionPercent(0);
      setRemainingTime(totalRecipeTime);
      return;
    }

    // Calculate time elapsed so far
    let timeElapsed = 0;
    for (let i = 0; i < activeStepId - 1; i++) {
      timeElapsed += steps[i].duration;
    }

    // Calculate remaining time (sum of current step and all future steps)
    let timeRemaining = 0;
    for (let i = activeStepId - 1; i < steps.length; i++) {
      timeRemaining += steps[i].duration;
    }

    // Calculate completion percentage
    const completion = Math.min(100, Math.round((timeElapsed / totalRecipeTime) * 100));
    
    setCompletionPercent(completion);
    setRemainingTime(timeRemaining);
  }, [activeStepId, steps, totalRecipeTime]);

  // Format time in hours and minutes
  const formatTimeToCompletion = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes} minutes`;
  };
  
  // Format time for individual steps
  const formatStepTime = (seconds) => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <div className="progress-sidebar">
      <div className="progress-header">
        <h3>Recipe Progress</h3>
        <div className="progress-bar-container">
          <div 
            className="progress-bar-fill" 
            style={{ width: `${completionPercent}%` }}
          ></div>
          <div className="progress-percentage">{completionPercent}%</div>
        </div>
      </div>

      <div className="time-to-bread" id="time-to-bread">
        <h4>Time to Bread</h4>
        <div className="countdown-display">
          {formatTimeToCompletion(remainingTime)}
        </div>
        <div className="completion-estimate">
          {activeStepId ? (
            <p>Currently on Step {activeStepId} of {steps.length}</p>
          ) : (
            <p>Start a step to begin timing</p>
          )}
        </div>
      </div>

      <div className="step-navigator">
        <h4>Step Navigator</h4>
        <ul className="step-list">
          {steps.map(step => (
            <li 
              key={step.id} 
              className={`step-item ${activeStepId === step.id ? 'active' : ''} ${activeStepId > step.id ? 'completed' : ''}`}
              onClick={() => {
                // Scroll to the step's runbook entry
                const stepElement = document.querySelector(`.step-item[data-step="${step.id}"]`);
                if (stepElement) {
                  stepElement.scrollIntoView({ behavior: 'smooth' });
                  // Expand the step if it's collapsed
                  if (!stepElement.classList.contains('active')) {
                    stepElement.querySelector('.step-header').click();
                  }
                }
              }}
            >
              <div className="step-number">{step.id}</div>
              <div className="step-content">
                <div className="step-name">{step.name}</div>
                <div className="step-time">{formatStepTime(step.duration)}</div>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

export default ProgressSidebar;